/**
 * Treatment Plans + Sittings API (Phase 1)
 *
 * Backend endpoints under /api/organisation (scoped to caller's org):
 *
 *   Plans
 *     POST   /plans                                    create single-visit plan
 *     GET    /plans/{plan_id}                          plan detail (plan + items + sittings + payments)
 *     GET    /plans?patient_id=&status=                list plans for patient
 *     PATCH  /plan-items/{plan_id}/{item_id}/discount  apply/update discount (atomic with audit)
 *
 *   Sitting state machine (doctor, nurse, receptionist — role gated at backend)
 *     POST   /plans/{plan_id}/sittings/{id}/assign     assign / reassign
 *     POST   /plans/{plan_id}/sittings/{id}/execute    assignee starts
 *     POST   /plans/{plan_id}/sittings/{id}/complete   assignee finishes
 *     POST   /plans/{plan_id}/sittings/{id}/approve    approver signs off
 *     POST   /plans/{plan_id}/sittings/{id}/reject     approver rejects, redo
 *     POST   /plans/{plan_id}/sittings/{id}/payment    collect payment (doctor or receptionist)
 *     POST   /plans/{plan_id}/sittings/{id}/cancel     cancel (cascades to appointment)
 *
 *   Sitting queries (for Procedure Queue, receptionist views, patient-arrival banner)
 *     GET    /sittings/assigned?status_prefix=         my procedure queue
 *     GET    /sittings/action-pending?timing=          receptionist action list
 *     GET    /sittings/scheduled?date=YYYY-MM-DD       upcoming calendar (GSI8)
 *     GET    /sittings/for-visit/{visit_id}            sittings on a visit (VMETA)
 *
 * Every function returns `{ success, data?, error? }`. Error messages
 * prefer the server's `detail` verbatim, with category-specific
 * defaults for well-known status codes. 409 STATE_MISMATCH responses
 * carry a structured `detail` object, so buildApiError surfaces the
 * message field explicitly.
 */

import apiClient from './apiClient';


// ── Error translator (Phase 1 variant — handles 409 STATE_MISMATCH) ──
function buildApiError(error, fallback) {
  const data = error?.response?.data;
  const status = error?.response?.status;

  // 409 STATE_MISMATCH comes back as { detail: { code, message } }
  const serverDetail = data?.detail;
  if (serverDetail && typeof serverDetail === 'object') {
    if (serverDetail.message) return serverDetail.message;
    return JSON.stringify(serverDetail);
  }
  if (typeof serverDetail === 'string') return serverDetail;
  if (data?.message) return data.message;

  switch (status) {
    case 400: return 'Invalid request — please check the form.';
    case 401: return 'Session expired. Please login again.';
    case 403: return 'You do not have permission for this action.';
    case 404: return 'The item could not be found.';
    case 409: return 'That action conflicts with the current state. Please refresh and try again.';
    case 422: return 'Invalid data — please check the form.';
    case 500: return 'Server error. Please try again in a moment.';
    default:  return fallback;
  }
}

function handleResult(response) {
  return { success: true, data: response.data };
}

function handleError(error, fallback) {
  console.error('[PatientPlanAPI]', error);
  // `status` is surfaced (additive, non-breaking) so callers that need to
  // distinguish guard rejections — e.g. the add-procedure flow mapping a
  // 403 out-of-department / 400 role-ineligible into a readable hint —
  // can, without re-parsing the axios error. `error` still carries the
  // server `detail`/`message` verbatim when present.
  return {
    success: false,
    error: buildApiError(error, fallback),
    status: error?.response?.status,
  };
}


// ═══════════════════════════════════════════════════════════════════════
//   Plans
// ═══════════════════════════════════════════════════════════════════════

/**
 * POST /api/organisation/plans
 * @param {Object} body — matches CreatePlanRequest on the backend
 *   patient_id, source_visit_id, procedure_id,
 *   estimated_cost?, discount_amount?, discount_reason?,
 *   tooth_numbers?, payment_timing?, timing_mode,
 *   scheduled_for?, assigned_to_member_id?, requires_approval?,
 *   approver_member_id?, remember_preferences?, source?,
 *   expires_at? (YYYY-MM-DD), standing? (bool — "no expiry till review date")
 */
export const createPlan = async (body) => {
  try {
    const r = await apiClient.post('/api/organisation/plans', body);
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to create plan.');
  }
};

/**
 * POST /api/organisation/plans/{plan_id}/items/{item_id}/perform
 * Perform a STANDING (repeatable) procedure again — creates the next sitting
 * under the same item on the current visit. No re-advice needed.
 */
export const performStandingItem = async (planId, itemId, visitId) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/items/${itemId}/perform`,
      { visit_id: visitId },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to perform procedure again.');
  }
};

/**
 * POST /api/organisation/plans/{plan_id}/items/{item_id}/review-date
 * Renew/extend a standing procedure's review date (doctor queue-widget action).
 */
export const updateItemReviewDate = async (planId, itemId, expiresAt) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/items/${itemId}/review-date`,
      { expires_at: expiresAt },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to update review date.');
  }
};

export const getPlanDetail = async (planId) => {
  try {
    const r = await apiClient.get(`/api/organisation/plans/${planId}`);
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load plan.');
  }
};

export const listPlansForPatient = async (patientId, { status } = {}) => {
  try {
    const params = { patient_id: patientId };
    if (status) params.status = status;
    const r = await apiClient.get('/api/organisation/plans', { params });
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load plans for patient.');
  }
};


// ═══════════════════════════════════════════════════════════════════════
//   PlanItem — discount
// ═══════════════════════════════════════════════════════════════════════

/**
 * PATCH /api/organisation/plan-items/{plan_id}/{item_id}/discount
 * Applies / updates a discount atomically with an AUDIT#DISCOUNT# row.
 * Receptionist role is hard-blocked at the backend (403).
 */
export const applyDiscount = async (planId, itemId, { discount_amount, discount_reason }) => {
  try {
    const r = await apiClient.patch(
      `/api/organisation/plan-items/${planId}/${itemId}/discount`,
      { discount_amount, discount_reason },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to apply discount.');
  }
};


// ═══════════════════════════════════════════════════════════════════════
//   Sitting state machine
// ═══════════════════════════════════════════════════════════════════════

/**
 * POST /api/organisation/plans/{plan_id}/sittings/{sitting_id}/assign
 * Body: { assigned_to_member_id, requires_approval?, approver_member_id?, reason? }
 * `reason` is required when reassigning (prev assignee != new).
 */
export const assignSitting = async (planId, sittingId, body) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/assign`,
      body,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to assign sitting.');
  }
};

/**
 * GET /api/organisation/plans/{plan_id}/sittings/{sitting_id}/assignable-staff
 *
 * (EPIC A / A2-S6) Server-side department-scoped assignable-staff list for a
 * sitting. The returned staff are ALREADY both role-eligible (doctors for
 * doctor procedures; nurses only where nurse_allowed) AND department-scoped
 * (the procedure's dept ∪ no-dept staff; other depts excluded). The caller
 * MUST render this list verbatim — no client-side re-filter (single source of
 * truth; the write guards enforce the same rule server-side).
 *
 * Response shape: { staff: [ { member_id, name, role } ], count }
 */
export const fetchAssignableStaff = async (planId, sittingId) => {
  try {
    const r = await apiClient.get(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/assignable-staff`,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load assignable staff.');
  }
};

/** POST …/execute — assignee marks started (payment_cleared|scheduled → in_progress) */
export const executeSitting = async (planId, sittingId) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/execute`,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to start sitting.');
  }
};

/**
 * POST …/complete — assignee marks done. Routes to pending_approval if
 * requires_approval, else completed (pay-before) or awaiting_payment
 * (pay-after unpaid).
 * Body: { completion_note? }
 */
export const completeSitting = async (planId, sittingId, body = {}) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/complete`,
      body,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to complete sitting.');
  }
};

/** POST …/approve — designated approver signs off. */
export const approveSitting = async (planId, sittingId) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/approve`,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to approve sitting.');
  }
};

/**
 * Phase 1f — POST …/approve-with-edit. Senior edits the junior's draft Rx,
 * approves the sitting, and supersedes the draft row in one call.
 * Body: { draft_meta_id, medicines, chief_complaint?, diagnosis?, doctor_notes? }
 */
export const approveWithEditedPrescription = async (
  planId,
  sittingId,
  { draft_meta_id, medicines, chief_complaint, diagnosis, doctor_notes } = {},
) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/approve-with-edit`,
      { draft_meta_id, medicines, chief_complaint, diagnosis, doctor_notes },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to approve with edited prescription.');
  }
};

/** POST …/reject — back to in_progress; `rejection_reason` (min 3 chars) required. */
export const rejectSitting = async (planId, sittingId, { rejection_reason }) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/reject`,
      { rejection_reason },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to reject sitting.');
  }
};

/** GET /sittings/active-clinic-wide — Phase 1c team board. */
export const listActiveClinicWide = async () => {
  try {
    const r = await apiClient.get('/api/organisation/sittings/active-clinic-wide');
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Could not load active-procedures board.');
  }
};


/** GET /visits/{visit_id}/billing-summary — Phase 1c unified bill. */
export const getVisitBillingSummary = async (visitId) => {
  try {
    const r = await apiClient.get(
      `/api/organisation/visits/${visitId}/billing-summary`,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Could not load visit billing summary.');
  }
};


/** GET /plans/{plan_id}/sittings/{sitting_id}/payments — Phase 1c. */
export const listPaymentsForSitting = async (planId, sittingId) => {
  try {
    const r = await apiClient.get(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/payments`,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Could not list payments for sitting.');
  }
};


/**
 * GET /payments/{payment_id}/receipt — Phase 1c procedure receipt PDF.
 *
 * Returns { blobUrl } which can be opened via FileViewerStore or handed
 * to window.open. Endpoint is idempotent — "Reprint" just re-fetches.
 */
export const openProcedureReceiptPdf = async (paymentId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/payments/${paymentId}/receipt`,
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    // Defer the FileViewerStore import so this module stays UI-agnostic
    // for non-browser consumers (tests).
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({
      blobUrl,
      fileType: 'pdf',
      title: 'Procedure receipt',
    });
    return { success: true, blobUrl };
  } catch (err) {
    return handleError(err, 'Could not generate receipt.');
  }
};


/**
 * GET /visits/{visitId}/referral-receipt — referral consultation receipt PDF.
 *
 * Fetches the receipt for the most recent resolved referral hop that had
 * a payment collected. Returns 404 if no such hop exists.
 */
export const openReferralReceiptPdf = async (visitId) => {
  try {
    const response = await apiClient.get(
      `/api/organisation/visits/${visitId}/referral-receipt`,
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({
      blobUrl,
      fileType: 'pdf',
      title: 'Referral receipt',
    });
    return { success: true, blobUrl };
  } catch (err) {
    return handleError(err, 'Could not generate referral receipt.');
  }
};


/**
 * POST /receipts/combined — PDF receipt(s) for one or more payment_ids.
 *
 * The backend renders one page per receipt_number (payments sharing a
 * receipt number print together, exactly as that receipt was issued) and
 * concatenates them into a single multi-page PDF. Works for a single
 * payment id too (one page).
 */
export const openCombinedReceiptPdf = async (paymentIds) => {
  try {
    const response = await apiClient.post(
      '/api/organisation/receipts/combined',
      { payment_ids: paymentIds },
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({
      blobUrl,
      fileType: 'pdf',
      title: 'Receipt',
    });
    return { success: true, blobUrl };
  } catch (err) {
    return handleError(err, 'Could not generate receipt.');
  }
};


/** GET …/approval-bundle — Phase 1b bundle for the senior's review modal. */
export const getApprovalBundle = async (planId, sittingId) => {
  try {
    const r = await apiClient.get(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/approval-bundle`,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load approval bundle.');
  }
};

/**
 * POST …/payment — collect payment. BOTH doctor and receptionist are
 * allowed (dual-collector rule). For future-dated sittings, optionally
 * pass appointment_slot_date + appointment_slot_time to atomically spawn
 * a BOOKED appointment.
 * Body: {
 *   amount, payment_mode: 'CASH'|'CARD'|'UPI',
 *   waiver_amount?, waiver_reason?,
 *   appointment_slot_date?, appointment_slot_time?,
 * }
 */
export const collectPayment = async (planId, sittingId, body) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/payment`,
      body,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to collect payment.');
  }
};

/** POST …/cancel — `reason` (min 3 chars) required. Cascades to linked appointment. */
export const cancelSitting = async (planId, sittingId, { reason }) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/cancel`,
      { reason },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to cancel sitting.');
  }
};

/**
 * POST /plans/{plan_id}/sittings/{sitting_id}/extend-expiry
 * Extend (or shorten) a planned procedure's validity date. Backend records
 * who extended it (audit). `expiresAt` is 'YYYY-MM-DD', today-or-later.
 */
export const extendExpirySitting = async (planId, sittingId, { expires_at }) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/sittings/${sittingId}/extend-expiry`,
      { expires_at },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to extend expiry.');
  }
};


/**
 * POST /plans/{plan_id}/exclude-optional/{step_id} (Phase 2.7)
 *
 * Reception or doctor opts a patient out of an OPTIONAL workflow step
 * post-attach. Cancels the linked sitting, marks the PlanItem cancelled,
 * appends step_id to plan.excluded_step_ids. Mandatory steps return 400.
 */
export const excludeOptionalStep = async (planId, stepId) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/exclude-optional/${stepId}`,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to exclude step.');
  }
};


// ═══════════════════════════════════════════════════════════════════════
//   Sitting queries
// ═══════════════════════════════════════════════════════════════════════

/** GET /sittings/assigned?status_prefix=&lens= — caller's procedure queue.
 *
 *  `lens` (Phase 4.0e):
 *    - omitted (default) → assignee + visit-doctor merged queue
 *    - 'delegated_by_me' → sittings the caller delegated to someone else,
 *                          scoped to today's visits, non-terminal only.
 */
export const listAssignedSittings = async ({ status_prefix, lens } = {}) => {
  try {
    const params = {};
    if (status_prefix) params.status_prefix = status_prefix;
    if (lens) params.lens = lens;
    const r = await apiClient.get('/api/organisation/sittings/assigned', { params });
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load your procedure queue.');
  }
};

/** GET /sittings/action-pending?timing=before|after */
export const listActionPendingSittings = async ({ timing } = {}) => {
  try {
    const params = {};
    if (timing) params.timing = timing;
    const r = await apiClient.get('/api/organisation/sittings/action-pending', { params });
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load action-pending sittings.');
  }
};

/** GET /sittings/scheduled?date=YYYY-MM-DD */
export const listScheduledSittings = async ({ date }) => {
  try {
    const r = await apiClient.get('/api/organisation/sittings/scheduled', {
      params: { date },
    });
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load scheduled sittings.');
  }
};

/** GET /sittings/for-visit/{visit_id} — VMETA-scoped; returns sittings attached to a visit.
 *  Pass { includeHistorical: true } to also return sittings whose live ownership
 *  has moved off this visit (read-only "moved to [next visit]" breadcrumbs). */
export const listSittingsForVisit = async (visitId, { includeHistorical = false } = {}) => {
  try {
    const r = await apiClient.get(`/api/organisation/sittings/for-visit/${visitId}`, {
      params: includeHistorical ? { include_historical: true } : undefined,
    });
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load sittings for visit.');
  }
};


// ═══════════════════════════════════════════════════════════════════════
//   Phase 2 — multi-item plans + registration suggestion
// ═══════════════════════════════════════════════════════════════════════

/**
 * POST /api/organisation/plans/multi
 * Body: { patient_id, source_visit_id?, plan_title?, items: [ { procedure_id,
 *         tooth_numbers?, estimated_cost_override?, discount_amount?,
 *         discount_reason? } ] }
 */
export const createMultiItemPlan = async (body) => {
  try {
    const r = await apiClient.post('/api/organisation/plans/multi', body);
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to create treatment plan.');
  }
};

/**
 * POST /api/organisation/plans/{plan_id}/cancel
 * Body: { reason } (min 3 chars)
 */
export const cancelPlan = async (planId, { reason }) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/cancel`,
      { reason },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to cancel treatment plan.');
  }
};

/** GET /api/organisation/plans/active?patient_id=... */
export const listActivePlansForPatient = async (patientId) => {
  try {
    const r = await apiClient.get('/api/organisation/plans/active', {
      params: { patient_id: patientId },
    });
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load active plans for patient.');
  }
};

/**
 * GET /api/organisation/plans/open-items?patient_id=...
 * Flat list of the patient's OPEN recommended procedures (not yet done) for
 * the appointment booking dropdown — includes accepted-but-unscheduled items
 * (unlike /plans/active, which requires a scheduled sitting).
 * @returns {{ success, data?: { patient_id, items: [{procedure_id, name, duration_minutes}], count } }}
 */
export const listOpenProcedureItems = async (patientId) => {
  try {
    const r = await apiClient.get('/api/organisation/plans/open-items', {
      params: { patient_id: patientId },
    });
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load open procedures for patient.');
  }
};

/** GET /api/organisation/plans/{plan_id}/remaining-items */
export const listRemainingPlanItems = async (planId) => {
  try {
    const r = await apiClient.get(
      `/api/organisation/plans/${planId}/remaining-items`,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load remaining plan items.');
  }
};

/**
 * POST /api/organisation/plans/{plan_id}/items/{item_id}/start-visit-sitting
 * Body: { visit_id, assigned_to_member_id?, requires_approval? }
 * Idempotent on (plan_id, item_id, visit_id).
 */
export const startVisitSittingForItem = async (
  planId,
  itemId,
  { visit_id, assigned_to_member_id, requires_approval } = {},
) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/items/${itemId}/start-visit-sitting`,
      { visit_id, assigned_to_member_id, requires_approval },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to start sitting for plan item.');
  }
};

/**
 * POST /api/organisation/visits/{visit_id}/attach-plan-items
 * Body: { item_refs: [ {plan_id, item_id} ], payment_mode: 'CASH'|'CARD'|'UPI' }
 * Returns aggregated per-item results (some may have status='error' on partial failure).
 */
export const attachPlanItemsToVisit = async (visitId, { item_refs, payment_mode }) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/visits/${visitId}/attach-plan-items`,
      { item_refs, payment_mode },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to attach plan items to registration.');
  }
};

/**
 * POST /api/organisation/plans/{plan_id}/items/{item_id}/schedule-appointment
 * Body: { slot_date: 'YYYY-MM-DD', slot_time: 'HH:MM' }
 * Phase 2.5 — books a calendar slot for an already-scheduled plan item's sitting.
 */
export const scheduleAppointmentForItem = async (planId, itemId, { slot_date, slot_time }) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/items/${itemId}/schedule-appointment`,
      { slot_date, slot_time },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to schedule appointment for plan item.');
  }
};


// ═══════════════════════════════════════════════════════════════════════
//   Phase 2.8 — PatientPlanPage + clinic-wide index
// ═══════════════════════════════════════════════════════════════════════

/** GET /api/organisation/plans/{plan_id}/detail — rich projection. */
export const fetchPlanDetail = async (planId) => {
  try {
    const r = await apiClient.get(`/api/organisation/plans/${planId}/detail`);
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load plan detail.');
  }
};

/** GET /api/organisation/plans/index?status=&owner_doctor_member_id=&scheduled_on=&q= */
export const listPatientPlans = async (filters = {}) => {
  try {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.owner_doctor_member_id) {
      params.owner_doctor_member_id = filters.owner_doctor_member_id;
    }
    if (filters.scheduled_on) params.scheduled_on = filters.scheduled_on;
    if (filters.q) params.q = filters.q;
    const r = await apiClient.get('/api/organisation/plans/index', { params });
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to load patient plans.');
  }
};

/** POST /plans/{plan_id}/collect-upfront-payment — Pattern C trigger. */
export const collectUpfrontPayment = async (planId, body) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/collect-upfront-payment`,
      body,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to collect upfront payment.');
  }
};

/** POST /plans/{id}/items/{iid}/start-and-collect — Pattern A. */
export const startAndCollect = async (planId, itemId, body) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/items/${itemId}/start-and-collect`,
      body,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to start step + collect payment.');
  }
};

/** POST /plans/{id}/items/{iid}/start-prepaid — Pattern C step trigger. */
export const startPrepaid = async (planId, itemId, body) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/items/${itemId}/start-prepaid`,
      body,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to start prepaid step.');
  }
};

/** POST /plans/{id}/items/{iid}/edit-step — open-items-only edits. */
export const editStep = async (planId, itemId, body) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/items/${itemId}/edit-step`,
      body,
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to edit step.');
  }
};

/** POST /plans/{id}/items/{iid}/cancel-step — single-step cancel + cascade. */
export const cancelStep = async (planId, itemId, { reason }) => {
  try {
    const r = await apiClient.post(
      `/api/organisation/plans/${planId}/items/${itemId}/cancel-step`,
      { reason },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to cancel step.');
  }
};

/** DELETE /plans/{plan_id} — cascade + physical delete. */
export const deletePlan = async (planId, { reason }) => {
  try {
    const r = await apiClient.delete(
      `/api/organisation/plans/${planId}`,
      { data: { reason } },
    );
    return handleResult(r);
  } catch (err) {
    return handleError(err, 'Failed to delete plan.');
  }
};


const patientPlanApi = {
  createPlan,
  getPlanDetail,
  listPlansForPatient,
  applyDiscount,
  assignSitting,
  executeSitting,
  completeSitting,
  approveSitting,
  rejectSitting,
  collectPayment,
  cancelSitting,
  listAssignedSittings,
  listActionPendingSittings,
  listScheduledSittings,
  listSittingsForVisit,
  // Phase 2
  createMultiItemPlan,
  cancelPlan,
  listActivePlansForPatient,
  listOpenProcedureItems,
  listRemainingPlanItems,
  startVisitSittingForItem,
  attachPlanItemsToVisit,
  // Phase 2.5
  scheduleAppointmentForItem,
  // Phase 2.8
  fetchPlanDetail,
  listPatientPlans,
  collectUpfrontPayment,
  startAndCollect,
  startPrepaid,
  editStep,
  cancelStep,
  deletePlan,
};

export default patientPlanApi;
